import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import * as i1 from '@upr-web-primer/storage';
import { StorageModule } from '@upr-web-primer/storage';
import * as i2 from 'ngx-cookie-service';
import { CookieService } from 'ngx-cookie-service';
import * as i3 from '@upr-web-primer/locale';
import { merge, split, isEmpty, get, toLower, map, trim, some, find } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';

// Geo Keys
const LOCAL_STORAGE_GEO_KEY = 'USR_SELECTED_RESIDENT_FILTER';
const GEO_COUNTRY_CODE = 'GEO_COUNTRY_CODE';
const GEO_REGION_CODE = 'GEO_REGION_CODE';
const GEO_NON_PRIMARY_RESIDENT_VALUE = 'non-primary';
const geoDefaultOptions = {
  GEOLOCATION_DISABLED: false,
  GEOLOCATION_AKAMAI: true,
  PRIMARY_GEO_REGION: 'US-FL,US-GA'
};
class GeolocationService {
  constructor(localStorage, cookieService, localeService, appDefaults) {
    this.localStorage = localStorage;
    this.cookieService = cookieService;
    this.localeService = localeService;
    this.appDefaults = appDefaults;
    this.geoLocation$ = new BehaviorSubject(this.getGeoLocationObject());
    this.appDefaults = merge(geoDefaultOptions, this.appDefaults);
    this.primaryGeoRegion = this.appDefaults.PRIMARY_GEO_REGION;
  }
  /**
   * Set geolocation the first time you open the website
   */
  initGeoLocation() {
    if (this.appDefaults.GEOLOCATION_DISABLED) {
      return;
    }
    return this.setGeoLocationAkamai();
  }
  /**
   * Override geolocation value to OUS.
   */
  setGeoLocationToNonResident() {
    if (this.appDefaults.GEOLOCATION_DISABLED) {
      return;
    }
    this.localStorage.setItem(LOCAL_STORAGE_GEO_KEY, GEO_NON_PRIMARY_RESIDENT_VALUE);
    this.geoLocation$.next(this.getGeoLocationObject());
  }
  /**
   * Override geolocation value to Primary Region.
   */
  setGeoLocationToResident() {
    if (this.appDefaults.GEOLOCATION_DISABLED) {
      return;
    }
    // udeploy format: US-FL,US-GA
    const configRegions = split(this.primaryGeoRegion, ',');
    const location = !!configRegions && configRegions.length > 0 ? configRegions[0] : GEO_NON_PRIMARY_RESIDENT_VALUE;
    this.localStorage.setItem(LOCAL_STORAGE_GEO_KEY, location);
    this.geoLocation$.next(this.splitGeoLocationAkamai(location));
  }
  /**
   * Remove geolocation value manually.
   * We should avoid on use this method at all time.
   */
  removeGeoLocation() {
    this.localStorage.removeItem(LOCAL_STORAGE_GEO_KEY);
  }
  filterByTcmPersona(collection) {
    if (isEmpty(collection)) {
      return [];
    }
    if (this.appDefaults.GEOLOCATION_DISABLED) {
      return collection;
    }
    return this.filterByTcmPersonaAkamai(collection);
  }
  getPersona(item) {
    return get(item, 'Persona.KeywordValues.0.Key') || get(item, 'Component.MetadataFields.Persona.KeywordValues.0.Key') || get(item, 'MetadataFields.Persona.KeywordValues.0.Key') || get(item, 'Persona') || '';
  }
  /**
   * Get the country manually. Even if the user is defined as OUS
   * we returned the geo country. Used for populating country dropdowns and other cases.
   */
  getGeoCountry() {
    return this.localStorage.getItem(GEO_COUNTRY_CODE) || this.localeService.getFromStorage().country || '';
  }
  /**
   * Get the geolocation value manually but in object form.
   * @return {GeoLocationType} Geolocation object containing country and region/state. If the user is OUS, then the country and region value will be the OUS const value
   */
  getGeoLocationObject() {
    const geo = this.getGeoLocation();
    return this.splitGeoLocationAkamai(geo);
  }
  /**
   * Get real geolocation value from cookies
   */
  getCookieLocation() {
    const akamaiCountry = this.cookieService.get(GEO_COUNTRY_CODE) || '';
    const akamaiRegion = this.cookieService.get(GEO_REGION_CODE) || '';
    return {
      country: akamaiCountry,
      region: akamaiRegion
    };
  }
  // private methods
  /**
   * Get geolocation value manually
   */
  getGeoLocation() {
    const value = this.localStorage.getItem(LOCAL_STORAGE_GEO_KEY);
    if (!value || value === 'null') {
      // this would mean we haven't set geolocation yet
      return null;
    }
    return value;
  }
  setGeoLocationAkamai(ignoreExistingGeo) {
    const akamaiCountry = this.cookieService.get(GEO_COUNTRY_CODE) || '';
    const akamaiRegion = this.cookieService.get(GEO_REGION_CODE) || '';
    // if we already have the location set to the local storage,
    // no need to set a new one (in case the user already changed this value)
    const geo = this.getGeoLocation();
    if (!!geo && !ignoreExistingGeo) {
      const newCountry = akamaiCountry || this.localeService.getFromStorage().country;
      this.localStorage.setItem(GEO_COUNTRY_CODE, newCountry);
      this.geoLocation$.next(this.splitGeoLocationAkamai(geo));
      return;
    }
    if (!akamaiCountry || !akamaiRegion) {
      this.localStorage.setItem(LOCAL_STORAGE_GEO_KEY, GEO_NON_PRIMARY_RESIDENT_VALUE);
      this.localStorage.setItem(GEO_COUNTRY_CODE, akamaiCountry || this.localeService.getFromStorage().country);
      this.geoLocation$.next(this.splitGeoLocationAkamai(GEO_NON_PRIMARY_RESIDENT_VALUE));
      return;
    }
    const newGeo = this.determineGeoLocationStatus(akamaiCountry, akamaiRegion);
    this.localStorage.setItem(LOCAL_STORAGE_GEO_KEY, newGeo);
    this.localStorage.setItem(GEO_COUNTRY_CODE, akamaiCountry);
    this.geoLocation$.next(this.splitGeoLocationAkamai(newGeo));
  }
  splitGeoLocationAkamai(location) {
    if (!location || location === GEO_NON_PRIMARY_RESIDENT_VALUE) {
      return {
        country: this.getGeoCountry(),
        region: GEO_NON_PRIMARY_RESIDENT_VALUE
      };
    }
    const parts = split(location, '-');
    return {
      country: parts[0] || '',
      region: parts[1] || ''
    };
  }
  filterByTcmPersonaAkamai(collection) {
    return collection.filter(item => {
      const componentPersona = this.getPersona(item);
      if (!componentPersona || componentPersona === 'All') {
        return true; // Show links without persona or 'All' persona
      }
      let hasGeo = !!this.getGeoLocation();
      if (hasGeo && this.getGeoLocation() === GEO_NON_PRIMARY_RESIDENT_VALUE) {
        hasGeo = false;
      }
      if (!hasGeo) {
        return toLower(componentPersona) === 'ous'; // Show NON FL resident offers by default
      }
      // udeploy format: US-FL,US-GA
      const configRegions = split(this.primaryGeoRegion, ',');
      const regions = map(configRegions, cr => {
        let region = '';
        const parts = split(cr, '-');
        if (parts && parts.length == 2) {
          region = parts[1];
        } else {
          region = cr;
        }
        return toLower(trim(region));
      });
      return some(regions, r => {
        return r === toLower(componentPersona);
      });
    });
  }
  determineGeoLocationStatus(country, region) {
    // udeploy format: US-FL,US-GA
    const configRegions = split(this.primaryGeoRegion, ',');
    // comparing if akamai matches with one of our udeploy configs
    const selected = find(configRegions, cr => {
      return toLower(trim(cr)) === toLower(`${trim(country)}-${trim(region)}`);
    });
    return selected || GEO_NON_PRIMARY_RESIDENT_VALUE;
  }
  static {
    this.ɵfac = function GeolocationService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GeolocationService)(i0.ɵɵinject(i1.LocalStorageService), i0.ɵɵinject(i2.CookieService), i0.ɵɵinject(i3.LocaleService), i0.ɵɵinject('AppDefaults'));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GeolocationService,
      factory: GeolocationService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GeolocationService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.LocalStorageService
  }, {
    type: i2.CookieService
  }, {
    type: i3.LocaleService
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['AppDefaults']
    }]
  }], null);
})();
class GeolocationModule {
  static {
    this.ɵfac = function GeolocationModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GeolocationModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: GeolocationModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [CookieService, GeolocationService, provideHttpClient(withInterceptorsFromDi())],
      imports: [CommonModule, StorageModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GeolocationModule, [{
    type: NgModule,
    args: [{
      imports: [CommonModule, StorageModule],
      providers: [CookieService, GeolocationService, provideHttpClient(withInterceptorsFromDi())]
    }]
  }], null, null);
})();

/*
 * Public API Surface of geolocation
 */

/**
 * Generated bundle index. Do not edit.
 */

export { GEO_COUNTRY_CODE, GEO_NON_PRIMARY_RESIDENT_VALUE, GEO_REGION_CODE, GeolocationModule, GeolocationService, LOCAL_STORAGE_GEO_KEY };
